import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import { withPartUpdate } from '@/helpers/rc/miners'
import { addReplacedComponents, deleteReplacedComponent, getComponents, getComponentTypes } from '@/services/rc.service'
import type { IComponent, IMinerDetails } from '@/types/rc/miners'
import { partTypes, type IndividualPart } from '@/types/rc/miners/PartType'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, ref, type Ref } from 'vue'

interface AddComponentsMutation {
  part_id: number
  components: IComponent[]
}

interface DeleteComponentMutation {
  part_id: number
  component_id: number
}

export const usePartReplacements = (
  minerId: Ref<number>,
  partName: Ref<IndividualPart>,
  partModel: Ref<string | undefined | null>,
  partVersion?: Ref<string | undefined>
) => {
  const client = useQueryClient()

  const partType = computed(() => partTypes[partName.value])

  const componentTypes = (enabled: Ref<boolean> = ref(true)) =>
    useQuery({
      enabled: () => !!partModel.value && enabled.value,
      queryKey: ['component-types', partType, partModel, partVersion],
      queryFn: () =>
        getComponentTypes(partType.value, partModel.value!, partVersion?.value).catch(
          NotifyErrorWrapper('Failed to fetch component types')
        ),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 10 * 60 * 1000,
    })

  const componentsFor = (componentType: Ref<string | undefined>) =>
    useQuery({
      queryKey: ['components-for', componentType, partType, partModel, partVersion],
      queryFn: () =>
        getComponents(partType.value, partModel.value!, componentType.value!, partVersion?.value).catch(
          NotifyErrorWrapper('Failed to fetch components for')
        ),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      // Don't run util componentType is set:
      enabled: () => !!componentType.value && !!partModel.value,
    })

  const addComponentsMutation = useMutation({
    mutationFn: ({ part_id, components }: AddComponentsMutation) =>
      addReplacedComponents(minerId.value, part_id, partType.value, components),
    onError: NotifyErrorWrapper('Failed to add components'),
    onSuccess: (data) => {
      client.setQueryData(['miner-details', minerId], (prev: IMinerDetails) =>
        withPartUpdate(prev, partName.value, (part) => ({ ...part, replaced_components: data.replaced_components }))
      )
      NotifySuccess('Components updated')
    },
  })

  const deleteComponentMutation = useMutation({
    mutationFn: ({ part_id, component_id }: DeleteComponentMutation) =>
      deleteReplacedComponent(minerId.value, part_id, partType.value, component_id),
    onError: NotifyErrorWrapper('Failed to delete component'),
    onSuccess: (data) => {
      client.setQueryData(['miner-details', minerId], (prev: IMinerDetails) =>
        withPartUpdate(prev, partName.value, (part) => ({ ...part, replaced_components: data.replaced_components }))
      )
      NotifySuccess('Component deleted')
    },
  })

  return { componentTypes, componentsFor, addComponentsMutation, deleteComponentMutation }
}
